import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import CommentSection from './components/CommentSection';
import ArticleCard from '@/pages/category/components/ArticleCard';
import { articleDetails } from '@/mocks/articleData';
import { allArticles } from '@/mocks/categoryData';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const fallbackArticle = Object.values(articleDetails)[0];
  const article = articleDetails[articleId] || fallbackArticle;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    if (!article) return;

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'article-schema';

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': article.title,
      'description': article.excerpt,
      'image': article.imageUrl,
      'author': {
        '@type': 'Person',
        'name': article.author,
      },
      'datePublished': article.date,
      'dateModified': article.date,
      'publisher': {
        '@type': 'Organization',
        'name': '51吃瓜网',
        'url': 'http://zwrn.cn/',
      },
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': `http://zwrn.cn/article/${article.id}`,
      },
    };
    schemaScript.textContent = JSON.stringify(schema);

    const existingArticle = document.getElementById('article-schema');
    if (existingArticle) existingArticle.remove();

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'breadcrumb-schema';

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': t('article.breadcrumb.home'),
          'item': 'http://zwrn.cn/',
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': article.category,
          'item': 'http://zwrn.cn/',
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'name': article.title,
        },
      ],
    };
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);

    const existingBreadcrumb = document.getElementById('breadcrumb-schema');
    if (existingBreadcrumb) existingBreadcrumb.remove();

    document.head.appendChild(schemaScript);
    document.head.appendChild(breadcrumbScript);

    return () => {
      const s = document.getElementById('article-schema');
      const b = document.getElementById('breadcrumb-schema');
      if (s) s.remove();
      if (b) b.remove();
    };
  }, [article, t]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 51吃瓜网`;
    } else {
      document.title = '文章不存在 - 51吃瓜网';
    }
  }, [article]);

  const currentIndex = article ? allArticles.findIndex((a) => a.id === articleId) : -1;
  const prevArticle = currentIndex > 0 ? allArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex >= 0 && currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return allArticles
      .filter((a) => a.categorySlug === article.categorySlug && a.id !== articleId)
      .slice(0, 3);
  }, [article, articleId]);

  if (!article) {
    return (
      <>
        <main>
          <Navbar />
          <div className="pt-24 md:pt-28 pb-16 min-h-screen flex items-center justify-center bg-background-50">
            <div className="text-center">
              <i className="ri-error-warning-line text-6xl text-foreground-300 mb-4 block"></i>
              <h1 className="font-heading text-2xl font-bold text-foreground-800 mb-2">文章不存在</h1>
              <p className="text-sm text-foreground-500 mb-6">这篇文章可能已被删除或链接无效</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-300"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: t('article.breadcrumb.home'), href: '/' },
    { label: article.category, href: '/' },
    { label: article.title },
  ];

  return (
    <>
      <main>
        <Navbar />
        <article className="pt-24 md:pt-28 pb-16 md:pb-24 bg-background-50">
          <BreadcrumbNav items={breadcrumbItems} />

          <div className="w-full px-6 md:px-10 lg:px-16 mt-6">
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-500/10 text-accent-700 text-xs font-semibold whitespace-nowrap mb-4">
                  {article.tag}
                </span>
                <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
                  {article.title}
                </h1>
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-secondary-100 overflow-hidden">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-full h-full object-cover"
                      title={article.author}
                    />
                  </div>
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <span>{t('article.published')} {article.date}</span>
                <span className="text-foreground-300">|</span>
                <span>{(article.views / 10000).toFixed(0)}万 {t('article.views')}</span>
              </div>
            </div>

            <div className="w-full mb-10 rounded-lg overflow-hidden">
              <div className="relative w-full aspect-[16/9] max-h-[500px]">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                  title={article.title}
                />
              </div>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="prose-custom">
                {article.content.map((paragraph, idx) => (
                  <p key={idx} className="text-base leading-relaxed text-foreground-700 mb-5">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground-800 mr-1">{t('article.tags')}:</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-8 border-t border-background-200/70">
                {prevArticle ? (
                  <Link
                    to="/"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="flex-1 flex items-center gap-3 p-4 rounded-lg bg-background-100 hover:bg-background-100/80 transition-colors duration-200 cursor-pointer group"
                  >
                    <i className="ri-arrow-left-line text-foreground-400 group-hover:text-primary-500 transition-colors w-5 h-5 flex items-center justify-center"></i>
                    <div className="min-w-0">
                      <div className="text-xs text-foreground-400 mb-1">{t('article.prevArticle')}</div>
                      <div className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors truncate">
                        {prevArticle.title}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}
                {nextArticle ? (
                  <Link
                    to="/"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="flex-1 flex items-center justify-end gap-3 p-4 rounded-lg bg-background-100 hover:bg-background-100/80 transition-colors duration-200 cursor-pointer group text-right"
                  >
                    <div className="min-w-0">
                      <div className="text-xs text-foreground-400 mb-1">{t('article.nextArticle')}</div>
                      <div className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors truncate">
                        {nextArticle.title}
                      </div>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 transition-colors w-5 h-5 flex items-center justify-center"></i>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}
              </div>

              <CommentSection articleId={articleId} />
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-12 border-t border-background-200/70">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                  {t('article.related')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}